import Vue from 'vue';
import fileTreeGenerator from '../file/file-tree-generator';
import remoteFileItemReader from '../file/remote-file-item-reader';
import binaryChecker from '../file/binary-checker';
import fileZipper from '../file/file-zipper';
import ProjectService from './project-service';
import config from '@/config';
import router from '../router';
import * as Sentry from '@sentry/browser';

export default {
  namespaced: true,

  state: {
    project: null,
    files: [],
    tree: [],
    content: null,
    loading: false,
    loadingContent: false,
    loadingDownload: false,
    loadingZip: false,
    previewUrl: null,
    previewUrlPooling: null,
    previewUrlFetching: false,
    previewUrlError: false,
    previewLogs: null,
  },

  getters: {
    project: (state) => state.project,
    files: (state) => state.files,
    tree: (state) => state.tree,
    content: (state) => state.content,
    loading: (state) => !!state.loading,
    loadingContent: (state) => !!state.loadingContent,
    loadingDownload: (state) => !!state.loadingDownload,
    loadingZip: (state) => state.loadingZip,
    previewUrlPoolingEnabled: (state) => {
      return (
        config.PREVIEW_URL_ENABLED &&
        !state.previewUrlError &&
        !state.previewUrl &&
        state.project &&
        state.project.presenter &&
        state.project.presenter.runnable &&
        state.project.buildId
      );
    },
    previewUrl: (state) => state.previewUrl,
    previewUrlPooling: (state) => state.previewUrlPooling,
    previewUrlFetching: (state) => state.previewUrlFetching,
    previewUrlError: (state) => state.previewUrlError,
    previewLogs: (state) => state.previewLogs,
  },

  mutations: {
    reset(state) {
      state.project = null;
      state.files = [];
      state.tree = [];
      state.content = null;
      state.loading = false;
      state.loadingContent = false;
      state.loadingDownload = false;
      state.loadingZip = false;
      state.previewUrl = null;
      state.previewUrlPooling = null;
      state.previewUrlFetching = false;
      state.previewUrlError = false;
      state.previewLogs = null;
    },

    project(state, payload) {
      state.project = payload;
    },

    files(state, payload) {
      state.files = payload || [];
      state.tree = fileTreeGenerator.generate(state.files);
    },

    content(state, payload) {
      state.content = payload;
    },

    loading(state, payload) {
      state.loading = !!payload;
    },

    loadingContent(state, payload) {
      state.loadingContent = !!payload;
    },

    loadingDownload(state, payload) {
      state.loadingDownload = !!payload;
    },

    loadingZip(state, payload) {
      state.loadingZip = !!payload;
    },

    clearPreviewUrlPooling(state) {
      state.previewUrl = null;
      state.previewUrlError = null;
      if (state.previewUrlPooling) {
        clearInterval(state.previewUrlPooling);
        state.previewUrlPooling = null;
      }
    },

    preview(state, payload) {
      if (!payload) {
        state.previewUrl = null;
        state.previewUrlError = false;
        return;
      }

      state.previewLogs = payload.logs;
      state.previewUrl = payload.url || null;
      state.previewUrlError = payload.status === 'ERROR';
    },

    previewUrlPooling(state, payload) {
      state.previewUrlPooling = payload;
    },

    previewUrlFetching(state, payload) {
      state.previewUrlFetching = !!payload;
    },
  },

  actions: {
    async setup(
      { commit, dispatch, rootGetters },
      payload,
    ) {
      commit('reset');
      commit('loading', true);

      const authenticated = await rootGetters[
        'auth/authenticatedAsync'
      ]();

      try {
        const { projectId } = payload;

        if (!authenticated) {
          commit('loading', false);
          router.push(
            `/auth/sign-in?referrer=/${projectId}`,
          );
          return;
        }

        const project = await ProjectService.find(
          projectId,
        );

        if (!project) {
          throw new Error('Not found');
        }

        commit('project', project);
        await dispatch('scaffold/set', project.scaffold, {
          root: true,
        });
        await dispatch(
          'builder/setup',
          {
            scaffoldId: project.scaffold.id,
            reload: true,
            originalProject: project,
          },
          { root: true },
        );
        commit('loading', false);
      } catch (error) {
        Sentry.captureException(error);
        commit('loading', false);
        router.push(`/`);
      }
    },

    async download({ getters, dispatch, commit }) {
      try {
        commit('loadingDownload', true);

        if (!getters.project.purchased) {
          const project = await ProjectService.purchase(
            getters.project.id,
          );

          commit('project', project);
        }

        commit('files', getters.project.build.files);
      } catch (error) {
        commit('loadingDownload', false);
        dispatch('core/error', error, { root: true });
      }
    },

    async zip({ getters, dispatch, commit }) {
      if (!getters.files) {
        return Promise.resolve();
      }

      commit('loadingZip', true);

      // Retry 3 times
      try {
        await fileZipper.downloadAndZip(getters.files);
      } catch (error) {
        try {
          await fileZipper.downloadAndZip(getters.files);
        } catch (error) {
          try {
            await fileZipper.downloadAndZip(getters.files);
          } catch (error) {
            Sentry.captureException(error);
            dispatch(
              'core/error',
              new Error(
                'Ops, an error occurred, please try again. If the error persists, please try again using another browser.',
              ),
              {
                root: true,
              },
            );
          }
        }
      } finally {
        commit('loadingZip', false);
      }
    },

    selectFromTree({ state, commit }, treeFile) {
      if (treeFile.directory) {
        return Promise.resolve();
      }

      const file = state.files.find(
        (current) => current.path === treeFile.path,
      );

      if (!file) {
        throw new Error(
          `File not found with path: ${
            treeFile ? treeFile.path : null
          }`,
        );
      }

      // TODO: Ugly, load extension from somewhere else
      file.extension = treeFile.extension;
      commit('loadingContent', true);

      // To clear previous item
      return Vue.nextTick()
        .then(() => {
          if (
            binaryChecker.isExtensionBinary(file.extension)
          ) {
            commit('loadingContent', false);
            return commit(
              'content',
              'No visualization available',
            );
          }

          return remoteFileItemReader.read(file);
        })
        .then(() => {
          commit(
            'content',
            file.content === undefined ? '' : file.content,
          );
          commit('loadingContent', false);
        })
        .catch(() => {
          commit('content', 'Error loading content.');
          commit('loadingContent', false);
        });
    },

    async startPreviewUrlPooling({ dispatch, commit }) {
      commit('clearPreviewUrlPooling');
      dispatch('fetchPreviewUrl');

      const previewUrlPooling = setInterval(() => {
        dispatch('fetchPreviewUrl');
      }, config.PREVIEW_URL_POOLING_INTERNVAL_IN_MILLIS);

      commit('previewUrlPooling', previewUrlPooling);
    },

    async clearPreviewUrlPooling({ commit }) {
      commit('clearPreviewUrlPooling');
    },

    async fetchPreviewUrl({ commit, getters }) {
      if (
        !getters.previewUrlFetching &&
        getters.previewUrlPoolingEnabled
      ) {
        commit('previewUrlFetching', true);

        ProjectService.refreshAndGetPreviewUrl(
          getters.project.buildId,
        )
          .then((preview) => {
            commit('preview', preview);
            commit('previewUrlFetching', false);
          })
          .catch(() => {
            commit('previewUrlFetching', false);
          });
      }
    },
  },
};
